import {IQuotationStatus} from "../../models";
import type { MarkQuotationStatusConfirmVariant } from "../quotation/MarkQuotationStatusConfirmPopup";

export const getMarkQuotationStatusConfirmVariant = (
    option: IQuotationStatus,
): MarkQuotationStatusConfirmVariant | null => {
    const trimmedLowerStatusName = String(option.quotation_status_name || "")
        .toLowerCase()
        .trim()

    if (
        option.quotation_status_id === 5
        || trimmedLowerStatusName === "confirmed"
    ) {
        return "confirmed"
    }

    if (
        option.quotation_status_id === 6
        || trimmedLowerStatusName === "lost"
    ) {
        return "lost"
    }

    return null
}

export const getFlagStatusChangeableValue = (statusId: number, statusName: string) => {
    if(!statusId || !statusName) return false;
    const trimmedLowerStatusName = statusName.toLowerCase().trim();

    if(statusId === 1 || trimmedLowerStatusName === "draft") return false;
    if(statusId === 2 || trimmedLowerStatusName === "ready to send") return true;
    if(statusId === 3 || trimmedLowerStatusName === "sent") return true;
    if(statusId === 4 || trimmedLowerStatusName === "follow up") return true;
    if(statusId === 5 || trimmedLowerStatusName === "confirmed") return false;
    if(statusId === 6 || trimmedLowerStatusName === "lost") return false;
    return false;
}

export const getStatusOptionsToShow = (statusId: number, statusOptions: IQuotationStatus[]) => {
    switch(statusId) {
        case 1: return [];
        case 2: return statusOptions.filter((sp) => sp.quotation_status_id === 3)
        case 3: return statusOptions.filter((sp) => [4, 5, 6].includes(sp.quotation_status_id))
        case 4: return statusOptions.filter((sp) => [5, 6].includes(sp.quotation_status_id))
        default: return [];
    }
}

export type DropdownPlacement =
    | "bottom-left"
    | "bottom-right"
    | "top-left"
    | "top-right"

const DROPDOWN_GAP = 4
const VIEWPORT_MARGIN = 8
export const DROPDOWN_MIN_WIDTH = 150

const PLACEMENT_PRIORITY: DropdownPlacement[] = [
    "bottom-left",
    "bottom-right",
    "top-left",
    "top-right",
]

const getPlacementPosition = (
    placement: DropdownPlacement,
    triggerRect: DOMRect,
    menuWidth: number,
    menuHeight: number,
) => {
    switch (placement) {
        case "bottom-left":
            return {
                top: triggerRect.bottom + DROPDOWN_GAP,
                left: triggerRect.left,
            }
        case "bottom-right":
            return {
                top: triggerRect.bottom + DROPDOWN_GAP,
                left: triggerRect.right - menuWidth,
            }
        case "top-left":
            return {
                top: triggerRect.top - DROPDOWN_GAP - menuHeight,
                left: triggerRect.left,
            }
        case "top-right":
            return {
                top: triggerRect.top - DROPDOWN_GAP - menuHeight,
                left: triggerRect.right - menuWidth,
            }
    }
}

const getPlacementOverflow = (
    top: number,
    left: number,
    menuWidth: number,
    menuHeight: number,
) => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    return (
        Math.max(0, VIEWPORT_MARGIN - top) +
        Math.max(0, VIEWPORT_MARGIN - left) +
        Math.max(0, top + menuHeight - (viewportHeight - VIEWPORT_MARGIN)) +
        Math.max(0, left + menuWidth - (viewportWidth - VIEWPORT_MARGIN))
    )
}

const placementFitsInViewport = (
    top: number,
    left: number,
    menuWidth: number,
    menuHeight: number,
) => getPlacementOverflow(top, left, menuWidth, menuHeight) === 0

export const resolveDropdownPlacement = (
    triggerRect: DOMRect,
    menuWidth: number,
    menuHeight: number,
) => {
    const candidates = PLACEMENT_PRIORITY.map((placement) => {
        const position = getPlacementPosition(
            placement,
            triggerRect,
            menuWidth,
            menuHeight,
        )

        return {
            placement,
            ...position,
            overflow: getPlacementOverflow(
                position.top,
                position.left,
                menuWidth,
                menuHeight,
            ),
        }
    })

    const fittingPlacement = candidates.find((candidate) =>
        placementFitsInViewport(
            candidate.top,
            candidate.left,
            menuWidth,
            menuHeight,
        )
    )

    const selected = fittingPlacement ?? candidates.reduce((best, candidate) =>
        candidate.overflow < best.overflow ? candidate : best
    )

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
            selected.top,
            viewportHeight - menuHeight - VIEWPORT_MARGIN,
        ),
    )
    const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
            selected.left,
            viewportWidth - menuWidth - VIEWPORT_MARGIN,
        ),
    )

    return {
        placement: selected.placement,
        top,
        left,
    }
}

export const PAGE_SCROLL_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ",
    "Spacebar",
])